import { PropsWithChildren, createContext, useContext, useState } from "react";

import {
  ConnectionsDocument,
  DatawarehouseMigrationDocument,
  EltSyncsDocument,
  ListSyncsDocument,
  useStartDatawarehouseMigrationMutation,
} from "@/apollo/types";
import { useToast } from "@/providers/ToastProvider";
import { toBase64 } from "@/utils/encoding";

import { DatawarehouseMigrationCompatibilityProvider } from "./MigrationCompatibilityProvider";
import { useCurrentMigration } from "./useCurrentMigration";

export type MigrationCompatibilityResult = {
  existingTables: { datasetId: string; tableId: string }[];
  existingViews: { datasetId: string; viewId: string }[];
  importedItems: { datasetId: string; itemId: string }[];
  datasetsInWrongLocation: {
    datasetId: string;
    location: string;
    expectedLocation: string;
  }[];
  compatible: boolean;
  error?: string;
};

const DataWarehouseMigrationContext = createContext<{
  currentMigration: ReturnType<typeof useCurrentMigration>;
  startMigration: (config: Record<string, any>) => Promise<void>;
  isStartingMigration: boolean;
  setDwConnectionPayload: (values: Record<string, any>) => void;
  dwConnectionPayload: Record<string, any>;
} | null>(null);

export function useDataWarehouseMigrationContext() {
  const context = useContext(DataWarehouseMigrationContext);
  if (!context) {
    throw new Error(
      "useDataWarehouseMigrationContext must be used within a DataWarehouseMigrationProvider",
    );
  }
  return context;
}

export function DatawarehouseMigrationProvider(props: PropsWithChildren) {
  const { start, isStartingMigration } = useStartMigration();
  const [dwConnectionPayload, setDwConnectionPayload] = useState<
    Record<string, any>
  >({});

  const currentMigration = useCurrentMigration();
  const ctx = {
    currentMigration,
    isStartingMigration,
    dwConnectionPayload,
    startMigration: start,
    setDwConnectionPayload,
  };

  return (
    <DataWarehouseMigrationContext.Provider value={ctx}>
      <DatawarehouseMigrationCompatibilityProvider
        dwConnectionPayload={dwConnectionPayload}
      >
        {props.children}
      </DatawarehouseMigrationCompatibilityProvider>
    </DataWarehouseMigrationContext.Provider>
  );
}

function useStartMigration() {
  const toast = useToast();

  const [startMigration, { loading: isStartingMigration }] =
    useStartDatawarehouseMigrationMutation({
      onError: (error) => {
        toast("Migration not started", error.message, "error");
      },
      refetchQueries: [
        { query: DatawarehouseMigrationDocument },
        { query: ConnectionsDocument },
        { query: EltSyncsDocument },
        { query: ListSyncsDocument },
      ],
    });

  const start = async (values: Record<string, any>) => {
    const payload = toBase64(values);
    startMigration({
      variables: {
        createConnectionInput: {
          integrationId: "bigquery",
          label: "_datawarehouse",
          payload: payload,
        },
      },
    });
  };

  return { start, isStartingMigration };
}
